// The stable page (/me), kept live: every move on a row posts without a page load and the
// stable re-renders in place; the page also re-renders itself whenever a bout of the
// stable's starts or ends, and each "in the ring now" entry ticks its turn count from the
// bout's own stream. The server stays the one source of truth: a refresh is a fresh /me,
// and only the [data-stable] region is swapped, so the account settings below are never
// disturbed. Without js every form still posts and redirects the old way.
import { $, setClock } from "./dom.ts";
import { parseDone, parseStarted, parseTurn } from "./wire.ts";
import { announce, toast } from "./toast.ts";
import { bell } from "./sound.ts";

/** A quiet moment before a passive refresh: several events in a burst become one fetch. */
const SETTLE_MS = 600;

/**
 * Which shelves are open, keyed by data-shelf, so a refresh keeps them the way you left them.
 */
export function openShelves(root: ParentNode): Record<string, boolean> {
  const open: Record<string, boolean> = {};
  root.querySelectorAll("details[data-shelf]").forEach((d) => {
    open[(d as HTMLElement).dataset.shelf || ""] = (d as HTMLDetailsElement).open;
  });
  return open;
}

export function restoreShelves(root: ParentNode, open: Record<string, boolean>) {
  root.querySelectorAll("details[data-shelf]").forEach((d) => {
    const k = (d as HTMLElement).dataset.shelf || "";
    if (k in open) (d as HTMLDetailsElement).open = open[k];
  });
}

/** Which rows have their pre-prompt unfolded, by slug. */
function openPrompts(root: ParentNode): Set<string> {
  return new Set(
    [...root.querySelectorAll("tr[data-prompt-row]:not([hidden])")].map(
      (tr) => (tr as HTMLElement).dataset.promptRow || "",
    ),
  );
}

function restorePrompts(root: ParentNode, open: Set<string>) {
  root.querySelectorAll("tr[data-prompt-row]").forEach((tr) => {
    const slug = (tr as HTMLElement).dataset.promptRow || "";
    (tr as HTMLElement).hidden = !open.has(slug);
    const btn = root.querySelector(`[data-prompt-toggle="${slug}"]`);
    if (btn) btn.textContent = open.has(slug) ? "prompt ▾" : "prompt ▸";
  });
}

/** The error page's one line, out of its html. */
export function errorLine(html: string) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.querySelector("main section p")?.textContent?.trim() || "that did not go through.";
}

/** Is someone typing in here? A passive refresh waits for them. */
const editing = (root: HTMLElement) => {
  const a = document.activeElement;
  return (
    !!a &&
    root.contains(a) &&
    (a instanceof HTMLInputElement || a instanceof HTMLSelectElement || a instanceof HTMLTextAreaElement)
  );
};

/** Everything the stable page holds open, torn down on leaving. */
class Streams {
  list: EventSource[];
  constructor() {
    this.list = [];
  }
  add(es: EventSource) {
    this.list.push(es);
  }
  closeAll() {
    this.list.forEach((e) => e.close());
    this.list = [];
  }
}

export function initStable(): { leave: () => void } {
  const root = $("[data-stable]") as HTMLElement | null;
  const streams = new Streams();
  const leave = () => streams.closeAll();
  if (!root || !window.fetch) return { leave };

  let busy = false; // one refresh at a time
  let wanted = false; // a refresh asked for while busy, or while someone was typing
  let settle: ReturnType<typeof setTimeout> | 0 = 0;

  const note = (msg: string) => {
    const el = $("[data-stable-note]", root);
    if (!el) return;
    el.textContent = msg;
    el.hidden = !msg;
  };

  /** Fetch and render the live VIP invite balance, if any. */
  const loadVipBalance = async () => {
    const el = root.querySelector("[data-vip-balance]") as HTMLElement | null;
    if (!el) return;
    try {
      const res = await fetch("/api/me/vip-balance", { cache: "no-store", credentials: "same-origin" });
      if (!res.ok) return;
      const data = await res.json();
      if (typeof data.remaining_usd === "number") {
        el.textContent = ` · $${data.remaining_usd.toFixed(2)} left on invite`;
        el.hidden = false;
      }
    } catch {
      /* ignore fetch failures; placeholder remains hidden */
    }
  };

  /**
   * Swap in a fresh stable. `html` is a whole /me page (the response to a move, or a plain
   * fetch); its [data-stable] replaces ours, shelves stay as they were.
   */
  const swap = (html: string) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const fresh = doc.querySelector("[data-stable]");
    if (!fresh) throw new Error("no stable in the page");
    const open = openShelves(root);
    const prompts = openPrompts(root);
    root.innerHTML = fresh.innerHTML;
    restoreShelves(root, open);
    restorePrompts(root, prompts);
    watchRing();
    loadVipBalance();
  };

  /** A fresh /me, when nobody is mid-edit; otherwise remembered for their blur. */
  const refresh = async () => {
    if (busy) {
      wanted = true;
      return;
    }
    if (editing(root)) {
      wanted = true;
      return;
    }
    busy = true;
    try {
      const res = await fetch("/me", { cache: "no-store", credentials: "same-origin" });
      if (res.redirected || !res.ok) {
        location.reload();
        return;
      } // signed out under us: the real page knows
      swap(await res.text());
    } catch (e) {
      console.warn("stable refresh", e);
    } finally {
      busy = false;
      if (wanted) {
        wanted = false;
        refresh();
      }
    }
  };
  const refreshSoon = () => {
    clearTimeout(settle);
    settle = setTimeout(refresh, SETTLE_MS);
  };

  /**
   * A move: post the form, and the page the server answers with (its redirect to /me,
   * followed) is the new stable. An error keeps the page as it is and says why, up top.
   */
  const move = async (form: HTMLFormElement) => {
    if (busy) return;
    busy = true;
    const buttons = form.querySelectorAll("button");
    buttons.forEach((b) => {
      b.disabled = true;
    });
    try {
      const body = new URLSearchParams([...new FormData(form)].map(([k, v]) => [k, String(v)])); // the form's fields, as text
      const res = await fetch(form.action, {
        method: "POST",
        body,
        credentials: "same-origin",
        cache: "no-store",
        headers: { "content-type": "application/x-www-form-urlencoded" },
      });
      const html = await res.text();
      if (!res.ok) {
        note(errorLine(html));
        return;
      }
      // the move landed somewhere other than the stable (an exhibition starts): go there
      if (!new URL(res.url, location.origin).pathname.startsWith("/me")) {
        location.href = res.url;
        return;
      }
      note("");
      swap(html);
    } catch (e) {
      console.warn("stable move", e);
      note("that did not go through. try again.");
    } finally {
      buttons.forEach((b) => {
        b.disabled = false;
      });
      busy = false;
      if (wanted) {
        wanted = false;
        refresh();
      }
    }
  };

  root.addEventListener("submit", (e) => {
    const form = e.target instanceof HTMLFormElement ? e.target : null;
    if (!form || !form.hasAttribute("data-stable-form")) return;
    e.preventDefault();
    if (form.dataset.confirm && !confirm(form.dataset.confirm)) return;
    move(form);
  });
  // a row's model select: an openrouter row fills from the page's one long list on first
  // focus (the html carries only the current model); any change is the move
  root.addEventListener("focusin", (e) => {
    const sel = e.target instanceof HTMLSelectElement && e.target.hasAttribute("data-fill") ? e.target : null;
    if (!sel || sel.dataset.filled) return;
    const tpl = document.getElementById(sel.dataset.fill || "");
    if (!(tpl instanceof HTMLTemplateElement)) return;
    const cur = sel.value;
    const opts = Array.from(tpl.content.querySelectorAll("option")).map(
      (o) => o.cloneNode(true) as HTMLOptionElement,
    ) as HTMLOptionElement[];
    if (!opts.some((o) => o.value === cur)) opts.unshift(sel.options[0].cloneNode(true) as HTMLOptionElement);
    sel.replaceChildren(...opts);
    sel.value = cur;
    sel.dataset.filled = "1";
  });
  root.addEventListener("change", (e) => {
    const sel = e.target instanceof HTMLSelectElement && e.target.hasAttribute("data-model-select") ? e.target : null;
    if (sel && sel.form) sel.form.requestSubmit();
  });
  // a row's pre-prompt folds open under it
  root.addEventListener("click", (e) => {
    const t = e.target instanceof Element ? e.target : null;
    const btn = t ? (t.closest("[data-prompt-toggle]") as HTMLElement | null) : null;
    if (!btn) return;
    const slug = btn.dataset.promptToggle || "";
    const open = openPrompts(root);
    if (open.has(slug)) open.delete(slug);
    else open.add(slug);
    restorePrompts(root, open);
  });
  // a shelf-wide move sits in the <summary>: the click must not also fold the shelf
  root.addEventListener("click", (e) => {
    const t = e.target instanceof Element ? e.target : null;
    const btn = t ? (t.closest("summary form.shelf-move button") as HTMLButtonElement | null) : null;
    if (!btn || !btn.form) return;
    e.preventDefault();
    btn.form.requestSubmit();
  });
  // a refresh that waited for a typist lands once they leave the field
  root.addEventListener("focusout", () => {
    if (wanted && !busy)
      setTimeout(() => {
        if (!editing(root)) {
          wanted = false;
          refresh();
        }
      }, 0);
  });

  /** the bouts in the ring this moment: each ticks its turn, and its ending is a refresh */
  const watched: Set<string> = new Set();
  const watchRing = () => {
    if (!window.EventSource) return;
    const ids = new Set(
      [...root.querySelectorAll("[data-live-bout]")].map((el) => (el as HTMLElement).dataset.liveBout || ""),
    );
    for (const id of ids) {
      if (!id || watched.has(id)) continue;
      watched.add(id);
      const es = new EventSource(`/bout/${id}/events`);
      streams.add(es);
      es.addEventListener("turn", (e) => {
        const m = parseTurn(e.data);
        if (!m) return;
        root
          .querySelectorAll(`[data-live-bout="${id}"] [data-turn]`)
          .forEach((el) => setClock(el as HTMLElement, m.turn));
      });
      es.addEventListener("done", (e) => {
        es.close();
        watched.delete(id);
        if (parseDone(e.data)) refreshSoon();
      });
      es.addEventListener("lagged", () => {
        es.close();
        watched.delete(id);
        refreshSoon();
      });
      es.onerror = () => {
        /* the browser retries */
      };
    }
  };
  watchRing();
  loadVipBalance();

  // every bout that opens anywhere: one of ours may be in it
  if (window.EventSource) {
    const es = new EventSource("/events");
    streams.add(es);
    es.addEventListener("started", (e) => {
      refreshSoon();
      // a stable you follow: say so, with a way there
      const ev = parseStarted(e.data);
      let following: string[] = [];
      try {
        following = JSON.parse(root.dataset.following || "[]");
      } catch {
        /* none */
      }
      const say = ev && announce(ev, following);
      if (say) {
        toast(say.html, say.href);
        bell();
      }
    });
    es.onerror = () => {
      /* the browser retries */
    };
  }
  return {
    leave: () => {
      clearTimeout(settle);
      leave();
    },
  };
}
